import type { UscStateType } from "../redux/store";
import type { VisualUscPriceType } from "./priceUtils";
import { defaultUscCashPrice } from "../../shared-logic/utils/uscPriceUtils";
import { ExternalPriceType, ExternalPriceValueType } from "./externalPriceTypes";
import { getUscLabel } from "./labels";
import { formatPriceIntl } from "../../../common-deprecated/priceUtils";
import type { UscEnv } from "../../shared-logic/utils/uscUtils";
import type { VehicleType } from "../../shared-logic/types/UscCommonTypes";

/**
 * ⚠️ Packs/Accessories context
 *
 * Before the PDP rework packs and accessories were merged together and rendered both as accessories. There was no distinction after formatting
 * Since the PDP rework packs and accessories were split up and rendered/shown differently, but you might find some leftovers of the previous setup
 * They now have separate types (UscAccessoryType + UscPackType), although they are still quite familiar
 * In the visualisation the packs and accessories are still rendered under a 'AccessoriesContainer' together
 * Also the settings for example 'enableAccessories' applies on both accessories and packs
 * Some naming also mentions accessories which could imply both accessories and packs.
 */

// ----------------------------------------------------------------------
// SHARED
// ----------------------------------------------------------------------

type PadocSalesChannelType = {
    visible: boolean;
    endDate: null | string;
    countryCode: string;
    channel: string;
    startDate: null | string;
    priority?: string;
};

type PadocEsTranslationType = {
    countryCode: string; // Capitalized
    languageCode: string; // Lowercase
    localisedApMarketingDescription: string;
    localisedApTechnicalDescription: string;
    localisedApMarketingName: string;
    localisedApTechnicalName: string;
};

// Parameters used to retrieve accessories/packs for a USC vehicle.
export type UscAccessoryPackParamType = {
    localConfigurationId: string;
    localModelCode: string;
    uscEnv: UscEnv;
};

/**
 * Helper to check if PADOC date strings are 'valid', meaning they are within the current date range.
 */
export const validateDate = (startDate: string | null, endDate: string | null): boolean => {
    if (!startDate && !endDate) return true;

    const dateCurrent = new Date();
    const dateStart = startDate ? new Date(startDate) : null;
    const dateEnd = endDate ? new Date(endDate) : null;

    if (!dateStart && dateEnd) return dateEnd > dateCurrent;
    if (!dateEnd && dateStart) return dateStart < dateCurrent;

    return dateCurrent > dateStart! && dateCurrent < dateEnd!;
};

/**
 * Get the priorityIndex from an array of salesChannels.
 * Closely linked with the ES query as well, so be sure to update accordingly.
 */
export const getPriorityIndex = (salesChannels: PadocSalesChannelType[], country: string): number => {
    const salesChannelResult = salesChannels.find((salesChannel) => {
        if (salesChannel.countryCode.toLowerCase() === country && salesChannel.channel === "Used Cars") {
            return validateDate(salesChannel.startDate, salesChannel.endDate);
        }
        return false;
    });

    return salesChannelResult?.priority ? Number(salesChannelResult.priority) : 10;
};

/**
 * Helper to get params used in USC accessory/pack queries.
 *
 * It is likely these params will expand over time, which would require only updating this helper.
 */
export const getUscAccessoryPackParams = (vehicle: VehicleType, uscEnv: UscEnv): UscAccessoryPackParamType => {
    const localConfigurationId =
        vehicle.product.productVersionIdentifiers.find(
            (identifier) => identifier.versionType.code === "A2P_LC_PADOC_ID",
        )?.versionCode || "";

    return {
        uscEnv,
        localModelCode: vehicle.product.localModelCode,
        localConfigurationId,
    };
};

/**
 * Retrieve the price of an accessory to be shown in the frontend.
 *
 * High level, an accessory price is calculated like this (this selector doesnt implement all of this but it seemed like the best place to mention the flow):
 * - Retrieve exclVat price from PADOC ES
 * - Add accessories to TMEF Prices execution, send exclVat price
 * - Get inclVAT prices from TMEF response
 * - Use inclVAT price to send to Makolab finance endpoints
 * - Retrieve financing price from Makolab finance response.
 *
 */
export const getAccessoryPackPriceSelector =
    (accessoryPack: UscAccessoryType | UscPackType) =>
    (state: UscStateType): VisualUscPriceType | null => {
        const { externalPrice, finance, commonSettings, uscSettings } = state;

        const { disableAccessoryPrice, disableAccessoryMonthlyPrice } = uscSettings;

        // Accessory prices are disabled, or
        // No accessory found or externalPrice doesnt have any data, return no price.
        // Accessories require TMEF to calculate prices.
        if (!accessoryPack || (!externalPrice.fetching && !externalPrice.data) || disableAccessoryPrice) return null;

        const cashPrice = { ...defaultUscCashPrice };

        // Try to find the accessory incl vat price in the TMEF response.
        const tmefPrice = externalPrice.data?.Prices.find((price) => {
            return price.Type === ExternalPriceType.Accessory && price.ID === accessoryPack.id;
        });
        cashPrice.cashPriceInclVat =
            tmefPrice?.Values.find((value) => value.Type === ExternalPriceValueType.InclVat)?.Amount || 0;

        cashPrice.cashPriceExclVat = accessoryPack.cashPriceExclVat;

        // No prices to show, return null. We dont support showing exclVat prices for accessories yet.
        if (!cashPrice.cashPriceInclVat) return null;

        // Retrieve finance price.
        let monthlyPrice = 0;
        let monthlyPriceFormatted = "";
        const accessoryMonthlyPrice = finance.accessories.find(
            (financeAccessory) => financeAccessory.accessoryId === accessoryPack.id,
        );
        if (accessoryMonthlyPrice && !disableAccessoryMonthlyPrice) {
            monthlyPrice = accessoryMonthlyPrice.monthlyPrice;
            monthlyPriceFormatted = getUscLabel(state, "uscDefaultMonthlyRateFormat", {
                price:
                    accessoryMonthlyPrice.monthlyPriceFormatted ||
                    // TES edge case: accessories don't have a formatted property...
                    // They said this will be fixed in the future, but I highly doubt this will be within the next 12 months.
                    formatPriceIntl(commonSettings, accessoryMonthlyPrice.monthlyPrice).primaryPrice, // TODO: OR-9072 support secondary price
            });
        }

        // Consider it still loading if we don't have a formatted monthly price yet
        // Otherwise, we'd first show loading -> empty -> formatted price
        const monthlyLoading =
            externalPrice.fetching || finance.fetching || (!disableAccessoryMonthlyPrice && !monthlyPriceFormatted);

        return {
            cashPrice,
            monthlyPrice,
            monthlyPriceFormatted,
            cashLoading: externalPrice.fetching,
            monthlyLoading,
            monthlyFailed: finance.failed || externalPrice.failed,
        };
    };

// ----------------------------------------------------------------------
// ACCESSORIES
// ----------------------------------------------------------------------

export type AccessoryPadocRetailerPrice = {
    error: boolean;
    prices: { country: string; currency: string; value: number }[];
};

// Only typed the properties we are actively using as there is a lot of data in there.
// This type covers what we _expect_ that ES will return, the actual returned value will depend on the queries used.
export type AccessoryPadocEsHitType = {
    _index: string;
    _type: string;
    _id: string;
    _score: number | null;
    _source: {
        finalImageUrl: string | null;
        salesChannels: PadocSalesChannelType[];
        accessory: {
            translations: PadocEsTranslationType[];
        };
        shortCode: string;
        finalCalculatedDealerInstallationTime: number;
        // Fields below are used specifically to send to Toshiko in context of the Products in the Basket feature
        // Parts is an array but we only ever use the first item.
        parts: {
            partNumber: string;
            // Contains more fields but we don't use them
        }[];
    };
    fields: {
        // eslint-disable-next-line camelcase
        national_recommended_accessory_retailer_price: string[]; // Stringified JSON, this parses to AccessoryPadocRetailerPrice[].
    };
};

export type AccessoryPadocEsResponseType = {
    hits: { hits: AccessoryPadocEsHitType[] };
};

export type UscAccessoryType = {
    id: string;
    shortCode: string; // This is an id commonly used by NMSCs to identify accessories. For now not used as *the* id yet but this might change.
    name: string;
    description: string;
    imageUrl: string | null;
    // Accessories are provided with only a cash price excl vat for now. Use price selectors to get the complete price of an accessory.
    cashPriceExclVat: number;
    // Each accessory or pack has a priority which can be anything between 0 and 10. Sourced from salesChannel properties in PADOC.
    priorityIndex: number;
    // Needed for TMEF to be able to calculate the labour cost of the accessory.
    installationTime: number;
    // Toshiko specific properties
    parts?: { partNumber: string }[];
};

/**
 * Format an accessory returned from the accessory_application-padoc PADOC ES index into a UscAccessory.
 */
export const formatUscAccessory = (
    country: string,
    language: string,
    esAccessory: AccessoryPadocEsHitType,
): UscAccessoryType => {
    const parsedTranslation = esAccessory._source.accessory.translations.find(
        (translationSource) =>
            translationSource.languageCode === language && translationSource.countryCode.toLowerCase() === country,
    );

    // Parse the price of the accessory.
    // This a hacky approach but unfortunately this is the only way for now.
    // Over time there will probably be a separate service handling accessory pricing.
    let cashPriceExclVat = 0;
    const padocPrice = esAccessory.fields.national_recommended_accessory_retailer_price[0]; // This field is an array, but we can assume only the first property is used.
    if (padocPrice) {
        // Because of technical reasons (dynamic fields in ES can only be strings) this field is stringified JSON.
        // No try/catch required as this function should already be wrapped.
        const parsedPrice: AccessoryPadocRetailerPrice = JSON.parse(padocPrice);

        // Accessories can be theoretically linked to multiple countries which is why we need to search for a specific country.
        // In practice this is probably an edge case.
        const parsedCountryPrice = parsedPrice.prices.find(
            (countryPrice) => countryPrice.country.toLowerCase() === country,
        );
        cashPriceExclVat = parsedCountryPrice?.value || 0;
    }

    // Retrieve the priorityIndex.
    const priorityIndex = getPriorityIndex(esAccessory._source.salesChannels, country);

    return {
        id: esAccessory._id,
        shortCode: esAccessory._source.shortCode,
        imageUrl: esAccessory._source.finalImageUrl || null,
        name: parsedTranslation?.localisedApMarketingName || "",
        description: parsedTranslation?.localisedApMarketingDescription || "",
        cashPriceExclVat,
        priorityIndex,
        installationTime: esAccessory._source.finalCalculatedDealerInstallationTime,

        // Toshiko specific properties
        parts: esAccessory._source.parts?.length ? esAccessory._source.parts : undefined,
    };
};

// ----------------------------------------------------------------------
// PACKS
// ----------------------------------------------------------------------

export type PackPadocEsHitType = {
    _index: string;
    _type: string;
    _id: string;
    _score: number | null;
    _source: {
        finalImageUrl: string | null;
        salesChannels: PadocSalesChannelType[];
        prices: {
            fromDate: string;
            countryCode: string;
            price: number;
            toDate: string;
            priceType: string;
            currency: string;
        }[];
        translations: {
            localisedPbShortDescription: string;
            countryCode: string;
            localisedPbMarketingName: string;
            languageCode: string;
            localisedPbMarketingDescription: string;
            localisedPbLongDescription: string;
        }[];
        applications: {
            applicationId: string;
            finalImageUrl: string;
            marketingName: string;
            shortCode: string;
            translations: {
                countryCode: string;
                languageCode: string;
                localisedApplMarketingName: string;
            }[];
        }[];
        shortCode: string;
        localDealerInstallationTime: number;

        // Fields below are used specifically to send to Toshiko in context of the Products in the Basket feature
        packProduct: {
            shortCode: string;
            // Contains more fields but we don't use them
        };
    };
};

export type PackPadocEsResponseType = {
    hits: { hits: PackPadocEsHitType[] };
};

export type UscPackItemType = {
    imageUrl: string | null;
    name: string;
};

export type UscPackType = {
    id: string;
    shortCode: string; // This is an id commonly used by NMSCs to identify packs/accessories. For now not used as *the* id yet but this might change.
    name: string;
    description: string;
    imageUrl: string | null;
    // Packs are provided with only a cash price excl vat for now. Use price selectors to get the complete price of an pack.
    cashPriceExclVat: number;
    // Each pack has a priority which can be anything between 0 and 10. Sourced from salesChannel properties in PADOC.
    priorityIndex: number;
    items: UscPackItemType[];
    // Needed for TMEF to be able to calculate the labour cost of the pack.
    installationTime: number;

    // Toshiko specific properties
    packProductShortCode?: string;
};

/**
 * Format a pack returned from the pack_application-padoc PADOC ES index into a UscPack.
 */
export const formatUscPack = (country: string, language: string, esPack: PackPadocEsHitType): UscPackType => {
    const parsedTranslation = esPack._source.translations.find(
        (translationSource) =>
            translationSource.languageCode.toLowerCase() === language &&
            translationSource.countryCode.toLowerCase() === country,
    );

    // Retrieve a price for the current country and date.
    const parsedPrice = esPack._source.prices.find((price) => {
        if (price.countryCode.toLowerCase() === country) return validateDate(price.fromDate, price.toDate);

        return false;
    });

    // Retrieve the priorityIndex.
    const priorityIndex = getPriorityIndex(esPack._source.salesChannels, country);

    // Parse the pack items
    const items = esPack._source.applications.map((application) => {
        const applicationTranslation = application.translations.find(
            (translationSource) =>
                translationSource.languageCode.toLowerCase() === language &&
                translationSource.countryCode.toLowerCase() === country,
        );

        return {
            name: applicationTranslation?.localisedApplMarketingName || "",
            imageUrl: application.finalImageUrl,
        };
    });

    return {
        id: esPack._id,
        shortCode: esPack._source.shortCode,
        name: parsedTranslation?.localisedPbMarketingName || "",
        cashPriceExclVat: parsedPrice?.price || 0,
        imageUrl: esPack._source.finalImageUrl,
        description: parsedTranslation?.localisedPbMarketingDescription || "",
        priorityIndex,
        items,
        installationTime: esPack._source.localDealerInstallationTime,

        // Toshiko expects to receive the shortCode of the packProduct, not the
        // "root" pack shortCode.
        packProductShortCode: esPack._source.packProduct?.shortCode || "",
    };
};
